<script>
  import { afterUpdate, onDestroy, onMount } from 'svelte';
  import {
    changeChannelStatus,
    listenToChannel,
    listenToChatPreview,
    listenToBlockedUsers,
    batchBlockComments,
    deleteCommentsFromTimestamp,
  } from '../../../Api/api';
  import { getTimeBefore, isAfter } from '../../../common/dates';
  import Button from '../../Common/Button.svelte';
  import { fade } from 'svelte/transition';
  import { navigate } from 'svelte-routing';
  import Comments from './Comments.svelte';
  import History from './History.svelte';
  import BlockedUsers from './BlockedUsers.svelte';

  export let id = '';
  export let minWidth640 = true;

  let unsubscribeListenToChat;
  let unsubscribeListenToChannel;
  let unsubscribeListenToBlockedUsers;
  let thread = [];
  let unfilteredThread = [];
  let threadHistory = [];
  let currentId = '';
  let scrollContainer;
  let scroller;
  let showHistory = false;
  let showBlockedUsers = false;
  let blockedUsersMap = {};
  let blockedUsers = [];

  $: autoScroll = false;
  $: channel = null;
  $: id && listenToChannelData(id);
  $: id && listenToNewChatData(id);
  $: blockedUsersMap && objectToArray();

  const objectToArray = () => {
    blockedUsers = Object.values(blockedUsersMap);
  };

  afterUpdate(() => {
    if (autoScroll && scroller && scrollContainer) {
      scrollContainer.scrollTop = scroller.scrollHeight - scrollContainer.clientHeight;
    }
  });

  onMount(async () => {
    unsubscribeListenToBlockedUsers = await listenToBlockedUsers((snapShot) => {
      blockedUsersMap = {};
      snapShot.forEach((doc) => {
        blockedUsersMap[doc.id] = doc.data();
      });
      thread = unfilteredThread.filter(
        (c) => !c.a && !c.d && !c.m && !c.blocked && !blockedUsersMap[c.userId],
      );
      blockComments(unfilteredThread);
    });
  });

  const blockComments = (comments) => {
    const badUserIds = comments
      .filter((c) => blockedUsersMap[c.userId])
      .reduce((acc, cur) => {
        if (!acc.includes(cur.userId)) {
          acc.push(cur.userId);
        }
        return acc;
      }, []);

    badUserIds.forEach((uid) => batchBlockComments(uid, id));
  };

  onDestroy(() => {
    if (unsubscribeListenToBlockedUsers) {
      unsubscribeListenToBlockedUsers();
    }
    if (unsubscribeListenToChat) {
      unsubscribeListenToChat();
    }
    if (unsubscribeListenToChannel) {
      unsubscribeListenToChannel();
    }
  });

  const onShowHistory = async () => {
    showHistory = !showHistory;
    showBlockedUsers = false;
  };

  const onShowBlockedUsers = async (e) => {
    showBlockedUsers = !showBlockedUsers;
    showHistory = false;
  };

  const listenToNewChatData = async (channelId) => {
    if (channelId !== currentId) {
      if (unsubscribeListenToChat) {
        unsubscribeListenToChat();
        unsubscribeListenToChat = null;
      }
      currentId = channelId;
      unsubscribeListenToChat = await listenToChatPreview(channelId, (snapShot) => {
        const chatData = [];
        snapShot.forEach((doc) => {
          chatData.push({ ...doc.data(), commentId: doc.id });
        });
        threadHistory = chatData
          .filter((c) => !!c.created && (c.a || c.d))
          .sort((a, b) => (isAfter(b.created.toDate(), a.created.toDate()) ? 1 : -1));
        unfilteredThread = chatData;
        thread = chatData.filter(
          (c) => !c.a && !c.d && !c.m && !c.blocked && !blockedUsersMap[c.userId],
        );
        blockComments(unfilteredThread);
      });
    }
  };

  const listenToChannelData = async (channelId) => {
    if (unsubscribeListenToChannel) {
      unsubscribeListenToChannel();
      unsubscribeListenToChannel = null;
    }
    unsubscribeListenToChannel = await listenToChannel(channelId, (snapShot) => {
      if (snapShot.exists) {
        channel = { ...snapShot.data(), id: snapShot.id };
      }
    });
  };

  const onGoLiveClicked = async (e, channel) => {
    e.stopPropagation();
    await changeChannelStatus(channel.id, !channel.live);
  };

  const onDeleteFromTimestamp = async () => {
    const timeBefore = getTimeBefore(new Date(), 15, 'minutes');
    await deleteCommentsFromTimestamp(channel.id, timeBefore.toDate());
  };
</script>

<div class="moderator-container" in:fade>
  <div class="header">
    <button class="backBtn" on:click={() => navigate('/chat')}
      ><span class="material-icons"> west </span></button
    >
    {#if channel}
      <h3>{channel.name}</h3>
      <div class="btn-container">
        {#if thread.length && !showHistory && !showBlockedUsers}
          <button on:click={onDeleteFromTimestamp}
            ><span class="material-icons">auto_delete </span>Slett over 15 minutter gamle</button
          >
        {/if}
        <button class:selected={showHistory} on:click={onShowHistory}
          ><span class="material-icons"> history </span>Historikk</button
        >
        <button class:selected={showBlockedUsers} on:click={onShowBlockedUsers}
          ><span class="material-icons"> person_off </span>Blokkerte brukere</button
        >
        <Button
          on:click={(e) => onGoLiveClicked(e, channel)}
          big={true}
          disabled={channel?.archived}
          red={channel.live}
        >
          {channel.live ? 'Steng chat' : 'Start chat'}
        </Button>
      </div>
    {/if}
  </div>
  <div class="mobile-header">
    <h3>MODERERING</h3>
    <div class="buttons">
      {#if thread.length && !showHistory && !showBlockedUsers}
        <button on:click={onDeleteFromTimestamp}
          ><span class="material-icons">auto_delete </span></button
        >
      {/if}
      <button on:click={onShowHistory} class:selected={showHistory}
        ><span class="material-icons">history </span></button
      >
      <button class:selected={showBlockedUsers} on:click={onShowBlockedUsers}
        ><span class="material-icons">person_off </span></button
      >
    </div>
  </div>

  <div class="comments-container" bind:this={scrollContainer}>
    <div class="comments" bind:this={scroller}>
      {#if showHistory}
        <History
          history={threadHistory}
          channelId={id}
          {currentId}
          on:close={() => (showHistory = !showHistory)}
        />
      {:else if showBlockedUsers}
        <BlockedUsers {blockedUsers} on:close={() => (showBlockedUsers = !showBlockedUsers)} />
      {:else if thread.length}
        <Comments {minWidth640} {thread} {currentId} />
      {:else}
        <div class="all-msg-ok">
          <p>Ingen meldinger å godkjenne.</p>
        </div>
      {/if}
    </div>
  </div>
</div>

<style src="./moderator.scss">
</style>
